<template>
  <div>
    <div v-if="value && value.length" class="menu-tree">
      <vuedraggable v-model="data" v-bind="draggableOptions" :move="onMove" @start="onDragStart" @end="onDragEnd" @change="onChange">
        <template v-for="(item, index) in data">
          <tree-item
            v-if="!item.children && !item.__children"
            :key="index"
            class="menu-tree__item"
            :class="`menu-${index}`"
            :selected="item === selectWidget"
            :is-tutorial="$store.state.site.tutorialEnable"
            :global="global"
            :dragging="dragging"
            :icon="item.getMenuInfo().icon"
            :index="item.id"
            :is-default="item.getMenuInfo().isDefault"
            :visible="item.visible"
            :toggle-display="item.getMenuInfo().toggleDisplay"
            :draggable="item.getMenuInfo().draggable"
            :info="item.getMenuInfo()"
            :label="$t(`model.${item.getMenuInfo().title}`)"
            @delete="onDelete(item, index)"
            @click.stop="handleItemClick(item, index)"
            @visible-change="onVisibleChange(item, index)"
          >
          </tree-item>
          <template v-else>
            <div :key="index" class="menu-tree__item menu-tree__item-sub-menu" :class="`menu-${index}`">
              <tree-item
                :ref="`treeItem${index}`"
                :is-leaf="(!!item.children  || (!!item.__children))"
                :label="$t(`model.${item?.getMenuInfo()?.title}`)"
                :icon="item.getMenuInfo().icon"
                :expand="selectKeys.includes(item.id)"
                :dragging="dragging"
                :is-default="item.getMenuInfo().isDefault"
                :is-tutorial="$store.state.site.tutorialEnable"
                :selected="item === selectWidget"
                :visible="item.visible"
                :global="global"
                :draggable="item.getMenuInfo().draggable"
                :info="item.getMenuInfo()"
                @delete="onDelete(item, index)"
                @click="handleItemClick(item, index)"
                @visible-change="onVisibleChange(item, index)"
                @leaf-click="handleLeafClick(item, index)">
              </tree-item>
              <el-collapse-transition>
                <div v-if="selectKeys.includes(item.id)" class="sub-menu-content" >
                  <vuedraggable v-model="item.children" v-bind="draggableOptions" :move="onMove" @end="(e) => onModelChildrenDragEnd(e, item)">
                    <div v-for="(child, childIndex) in (item.children || item.__children)" :key="childIndex" class="sub-menu-item flex items-center justify-between cursor-pointer hover:bg-fillColor-3" :class="{ 'active': $store.state.editor.subId === child.id, 'hide': Object.hasOwn(child, 'visible') && !child.visible }" @click.stop="handleSubItemClick(item, child, index, childIndex)">
                      <span v-if="child.menuName">{{ child.menuName }}</span>
                      <span v-else-if="child.getMenuInfo">{{$t('model.' + child.getMenuInfo().title)}}{{child.getMenuInfo().indexShow? childIndex + 1: ''}}</span>
                      <span v-else>{{ item.getMenuInfo().indexShow ? `${child.name}${childIndex + 1}` : child.name }}</span>
                      <div class="action-group">
                        <div v-if="!global" class="action-btn drag move-btn" title="Drag to reorder">
                          <i class="el-icon-rank icon-svg"></i>
                        </div>
                        <div 
                          v-if="child.getMenuInfo?.()?.showEye" 
                          class="action-btn visibility" 
                          :title="child.visible ? 'Hide' : 'Show'"
                          @click.stop="handleToggleChildVisible(item, child)"
                        >
                          <i 
                            :class="child.visible ? 'el-icon-view' : 'el-icon-view'" 
                            class="icon-svg" 
                            :style="{ opacity: child.visible ? 1 : 0.5 }"
                          ></i>
                        </div>
                        <div 
                          v-if="child?.getMenuInfo?.()?.showDelete" 
                          class="action-btn delete" 
                          title="Delete"
                          @click.stop="handleRemoveChild(item, child)"
                        >
                          <i class="el-icon-delete icon-svg"></i>
                        </div>
                      </div>
                    </div>
                  </vuedraggable>
                  <div v-if="item.addMenuMethod" class="add-nav" @click="handleAddMethod(item)">
                    <div class="btn">
                      <icon-upload />
                      <span class="text-12">{{ $t('head.addnav') }}</span>
                    </div>
                  </div>
                  <Popover
                    v-if="item.__subMenus"
                    placement="right-end"
                    popper-class="left-menu-add-popper"
                    width="auto"
                    :visible-arrow="false"
                    trigger="click">
                    <div class="menu-add-list">
                      <button v-for="(sc,scIndex) in item.__subMenus" :key="scIndex" class="menu-add-item" @click="addMenuItem(item,sc.model)">
                        {{$t(`model.${sc.title}`)}}
                      </button>
                    </div>
                    <div slot="reference" class="add-nav">
                      <div class="btn">
                        <icon-upload />
                        <span class="text-12">{{ $t('head.addnav') }}</span>
                      </div>
                    </div>
                  </Popover>
                </div>
              </el-collapse-transition>
            </div>
          </template>
        </template>
      </vuedraggable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance, nextTick } from 'vue'
import { Popover, MessageBox } from 'element-ui'
import vuedraggable from 'vuedraggable'
import { cloneDeep } from 'lodash-es'
import treeItem from './item'
import { disabledSortList } from "~/config/draggable"
import Bus from '~/site/model/bus'
import iconUpload from '@/assets/svg/icon/iconUpload.svg'
import { insertSortStep, insertStep } from "~/utils/event"

const props = defineProps({
  value: {
    type: Array,
    default: () => []
  },
  global: {
    type: Boolean,
    default: false
  },
  selectWidget: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['input', 'item-click'])
const { proxy } = getCurrentInstance()
const store = proxy.$store

const data = ref(props.value)
const selectKeys = ref([])
const dragging = ref(false)

const draggableOptions = computed(() => ({
  animation: 120,
  handle: '.move-btn'
}))

watch(() => props.value, (n) => {
  data.value = n
}, { deep: true })

const scrollToView = () => {
  const item = props.selectWidget
  const menuInfo = item?.getMenuInfo() || {}
  const noGlobal = item?.id && !props.global
  if (noGlobal && !menuInfo.disableSelectToView) {
    const dom = document.getElementById(item.id)
    const $wrap = document.querySelector('.edit-content__center--wrapper')
    $wrap.scrollTo({
      top: dom && dom.offsetTop || 0,
      behavior: 'smooth'
    })
  }
}

const expandAll = () => {
  data.value.forEach(item => {
    if (item.children || item.__children) {
      selectKeys.value.push(item.id)
    }
  })
}

const init = () => {
  expandAll()
}

onMounted(() => {
  init()
})

const handleAddMethod = (item) => {
  insertStep(proxy, [item.id, 'children'], cloneDeep(item.children), () => {
    item.__refresh && item.__refresh()
  })
  store.commit('editor/CLEAR_STORAGE_STEP')
  item.addMenuMethod && item.addMenuMethod()
}

const addMenuItem = (item, model) => {
  insertStep(proxy, [item.id, 'children'], cloneDeep(item.children))
  store.commit('editor/CLEAR_STORAGE_STEP')
  item.addMenuItem(model)
  document.body.click()
}

const handleLeafClick = (item) => {
  if (selectKeys.value.includes(item.id)) {
    const index = selectKeys.value.indexOf(item.id)
    selectKeys.value.splice(index, 1)
  } else {
    selectKeys.value.push(item.id)
  }
}

const deleteChildModel = (parent, child) => {
  const childIndex = parent.children.findIndex(item => item.id === child.id)
  if (childIndex !== -1) {
    parent.children.splice(childIndex, 1)
  }
}

const handleRemoveChild = (parent, child) => {
  MessageBox.confirm('此操作将删除该子菜单, 是否继续?', '提示', {
    confirmButtonText: '删除',
    cancelButtonText: '保留',
    customClass: 'delete-message-box',
    type: 'error',
    cancelButtonClass: 'cancel message-btn',
    confirmButtonClass: 'confirm message-btn'
  }).then(() => {
    deleteChildModel(parent, child)
  })
}

const handleToggleChildVisible = (parent, model) => {
  model.visible = !model.visible
}

const onVisibleChange = (model) => {
  insertStep(proxy, ['visible'], model.visible)
  model.visible = !model.visible
}

const handleItemClick = (item, index) => {
  proxy.SiteMenu.close()
  store.commit('editor/SET_SUB_ID', '')
  store.commit('user/CLEAR_SITE_LOGIN_USER_MODAL')
  emit('item-click', item, index)
  nextTick(() => {
    // item.__onMenuSelected && item.__onMenuSelected({ child: item }, proxy)
    scrollToView()
  })
}

const onDelete = (item, index) => {
  handleItemClick(item, index)
  nextTick(() => {
    proxy.SiteMenu.openDeleteBlockDialog()
  })
}

const onDragStart = (evt) => {
  dragging.value = true
}

const onDragEnd = () => {
  dragging.value = false
}

const onModelChildrenDragEnd = (e, model) => {
  dragging.value = false
  model.__onMenuDragEnd && model.__onMenuDragEnd()
  nextTick(() => {
    Bus.$emit('drag-end')
  })
}

const onMove = (evt) => {
  // 用固定的模块标识 getMenuInfo().title 判断，而不是用户可编辑的 .title
  const title = evt.relatedContext.element?.getMenuInfo?.().title || null
  return !disabledSortList.includes(title)
}

const handleSubItemClick = (model, child, index, childIndex) => {
  handleItemClick(model, index)
  store.commit('editor/SET_SUB_ID', child.__id || child.id)
  const props = cloneDeep({ child, index, childIndex })
  store.commit('user/CLEAR_SITE_LOGIN_USER_MODAL')
  model.__onMenuSelected && model.__onMenuSelected(props, proxy)
  nextTick(() => {
    child.__onMenuSelected && child.__onMenuSelected(props, proxy)
  })
  if (child.getMenuInfo) {
    const menu = {
      key: child.getMenuInfo().title,
      target: child,
      config: {
        childIndex
      },
      model,
    }
    proxy.SiteMenu.open(menu)
  }
}

const onChange = (eventData) => {
  const { moved } = eventData
  if (moved) {
    nextTick(() => {
      insertSortStep(proxy, [moved.element.id, moved.newIndex, moved.oldIndex])
      setTimeout(() => {
        scrollToView()
      }, 0)
    })
  }
  emit('input', data.value)
}
</script>

<style lang="less">
.menu-tree {
  padding: 0 4px;
  
  &__item {
    margin-bottom: 4px;
  }

  &__item-sub-menu {
    .sub-menu-content {
      padding-left: 12px;
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        left: 20px;
        top: 0;
        bottom: 14px;
        width: 1px;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02));
      }

      .sub-menu-item {
        height: 34px;
        padding-left: 24px;
        background: transparent;
        border: 1px solid transparent;
        font-size: 13px;
        border-radius: 8px;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 2px;
        transition: all 0.2s cubic-bezier(0.2, 0, 0, 1);
        position: relative;
        padding-right: 8px; // Add padding for actions

        &::before {
          content: '';
          position: absolute;
          left: -4px;
          top: 50%;
          width: 8px;
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
          transition: width 0.2s;
        }

        .action-group {
          display: flex;
          align-items: center;
          gap: 2px;
          opacity: 0;
          transform: translateX(10px);
          transition: all 0.2s ease;
          
          .action-btn {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
            color: rgba(255, 255, 255, 0.5);
            background: transparent;

            .icon-svg {
              font-size: 14px;
              transition: all 0.2s ease;
            }

            &:hover {
              background: rgba(255, 255, 255, 0.1);
              color: #fff;
              transform: scale(1.05);
            }

            &.delete:hover {
              background: rgba(245, 108, 108, 0.15);
              color: #f56c6c;
            }

            &.visibility:hover {
              background: rgba(var(--primary-color-rgb), 0.15);
              color: var(--primary-color);
            }
          }
        }

        &:hover {
          color: #fff;
          background: rgba(255, 255, 255, 0.04);
          
          &::before {
            background: rgba(255, 255, 255, 0.3);
            width: 12px;
          }

          .action-group {
            opacity: 1;
            transform: translateX(0);
          }
        }

        &.active {
          background: rgba(var(--primary-color-rgb), 0.1);
          color: var(--primary-color);
          font-weight: 500;

          &::before {
            background: var(--primary-color);
            width: 12px;
          }
        }

        &.hide {
          opacity: 0.5;
          text-decoration: line-through;
        }
      }
    }
  }

  .add-nav {
    width: 100%;
    margin-top: 12px;
    padding: 0 12px;
    
    .btn {
      height: 32px;
      width: 100%;
      border: 1px dashed rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      
      svg {
        width: 14px;
        height: 14px;
        fill: rgba(255, 255, 255, 0.4);
        margin-right: 6px;
        transition: all 0.2s;
      }

      span {
        color: rgba(255, 255, 255, 0.4);
        font-size: 12px;
        transition: all 0.2s;
      }

      &:hover {
        border-color: var(--primary-color);
        background: rgba(var(--primary-color-rgb), 0.05);
        
        svg {
          fill: var(--primary-color);
        }
        
        span {
          color: var(--primary-color);
        }
      }
    }
  }
}

.left-menu-add-popper {
  background: #1e1e20 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 40px -12px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  padding: 4px;
  
  .menu-add-item {
    display: block;
    width: 100%;
    padding: 8px 12px;
    text-align: left;
    color: rgba(255, 255, 255, 0.8);
    font-size: 13px;
    border-radius: 4px;
    transition: all 0.15s;
    
    &:hover {
      background: var(--primary-color);
      color: #fff;
    }
  }
  
  .popper__arrow {
    display: none;
  }
}
</style>